import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
    clearUpdateCampaignContextState,
    updateCampaignContext,
} from "../../store/outreachSlice";

const SUCCESS_MESSAGE =
    "Campaign context modified successfully. Remaining agent drafts will reflect these changes.";
const FALLBACK_ERROR = "Failed to update campaign context. Please try again.";

const hasValue = (value) =>
    value !== undefined && value !== null && String(value).trim() !== "";

const getCampaignId = (campaign = {}) =>
    campaign?.sequence_id || campaign?.id || "";

const getContextValue = (campaign = {}, detailCampaign = {}, key) => {
    const campaignContextValue = campaign?.campaign_context?.[key];
    const detailContextValue = detailCampaign?.campaign_context?.[key];

    if (hasValue(campaign?.[key])) return campaign[key];
    if (hasValue(campaignContextValue)) return campaignContextValue;
    if (hasValue(detailCampaign?.[key])) return detailCampaign[key];
    if (hasValue(detailContextValue)) return detailContextValue;

    return "";
};

const EditCampaignContextModal = ({
    isOpen,
    campaign,
    onClose,
    onUpdated,
}) => {
    const dispatch = useDispatch();
    const objectiveRef = useRef(null);
    const requestInFlight = useRef(false);
    const [campaignObjective, setCampaignObjective] = useState("");
    const [productValueProposition, setProductValueProposition] =
        useState("");
    const [toneSetting, setToneSetting] = useState("");
    const [formError, setFormError] = useState("");
    const {
        selectedCampaign,
        updateCampaignContextStatus,
        updateCampaignContextError,
    } = useSelector((state) => state.outreach);

    const sequenceId = getCampaignId(campaign);
    const selectedCampaignId = getCampaignId(selectedCampaign);
    const matchingCampaignDetails = useMemo(
        () =>
            sequenceId && selectedCampaignId === sequenceId
                ? selectedCampaign
                : {},
        [selectedCampaign, selectedCampaignId, sequenceId],
    );
    const isSaving = updateCampaignContextStatus === "loading";
    const currentError =
        formError ||
        (updateCampaignContextStatus === "failed"
            ? updateCampaignContextError || FALLBACK_ERROR
            : "");

    const handleClose = useCallback(() => {
        if (isSaving) return;

        requestInFlight.current = false;
        setFormError("");
        dispatch(clearUpdateCampaignContextState());
        onClose?.();
    }, [dispatch, isSaving, onClose]);

    useEffect(() => {
        if (!isOpen) {
            requestInFlight.current = false;
            return;
        }

        dispatch(clearUpdateCampaignContextState());
        setFormError("");
        setCampaignObjective(
            getContextValue(
                campaign,
                matchingCampaignDetails,
                "campaign_objective",
            ),
        );
        setProductValueProposition(
            getContextValue(
                campaign,
                matchingCampaignDetails,
                "product_value_proposition",
            ),
        );
        setToneSetting(
            getContextValue(campaign, matchingCampaignDetails, "tone_setting"),
        );

        window.setTimeout(() => {
            objectiveRef.current?.focus();
        }, 0);
    }, [campaign, dispatch, isOpen, matchingCampaignDetails]);

    useEffect(() => {
        if (!isOpen) return undefined;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const handleEscape = (event) => {
            if (event.key === "Escape") {
                handleClose();
            }
        };

        window.addEventListener("keydown", handleEscape);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener("keydown", handleEscape);
        };
    }, [handleClose, isOpen]);

    if (!isOpen) return null;

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (requestInFlight.current || isSaving) return;

        const trimmedObjective = campaignObjective.trim();
        const trimmedValueProposition = productValueProposition.trim();
        const trimmedTone = toneSetting.trim();

        if (
            !trimmedObjective ||
            !trimmedValueProposition ||
            !trimmedTone
        ) {
            setFormError(
                "Please fill all campaign context fields before saving.",
            );
            return;
        }

        if (!sequenceId) {
            setFormError("Invalid campaign id.");
            return;
        }

        setFormError("");
        requestInFlight.current = true;

        try {
            const response = await dispatch(
                updateCampaignContext({
                    sequenceId,
                    campaign_objective: trimmedObjective,
                    product_value_proposition: trimmedValueProposition,
                    tone_setting: trimmedTone,
                }),
            ).unwrap();
            const successMessage =
                (typeof response === "string" ? response : response?.message) ||
                SUCCESS_MESSAGE;

            dispatch(clearUpdateCampaignContextState());
            onUpdated?.(successMessage, sequenceId);
            onClose?.();
        } catch {
            // Redux state renders the backend error in the modal.
        } finally {
            requestInFlight.current = false;
        }
    };

    return (
        <div
            className="app-fade-in fixed inset-0 z-[80] flex items-center justify-center overflow-y-auto bg-slate-900/40 p-3 backdrop-blur-sm sm:p-4"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                    handleClose();
                }
            }}
        >
            <section
                role="dialog"
                aria-modal="true"
                aria-labelledby="edit-campaign-context-title"
                className="app-modal-enter flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl"
                onMouseDown={(event) => event.stopPropagation()}
            >
                <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
                    <div className="min-w-0">
                        <h2
                            id="edit-campaign-context-title"
                            className="text-xl font-bold text-slate-900"
                        >
                            Edit Campaign Context
                        </h2>
                        <p className="mt-1 text-sm text-slate-600">
                            Changes will apply to remaining agent drafts only.
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={handleClose}
                        disabled={isSaving}
                        aria-label="Close"
                        title="Close"
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-50 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <X size={20} aria-hidden="true" />
                    </button>
                </header>

                <form
                    id="edit-campaign-context-form"
                    onSubmit={handleSubmit}
                    className="campaign-scrollbar min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-5"
                >
                    {currentError && (
                        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                            {currentError}
                        </div>
                    )}

                    <div>
                        <label
                            htmlFor="campaign-context-objective"
                            className="mb-1.5 block text-sm font-semibold text-slate-700"
                        >
                            Campaign Objective
                        </label>
                        <textarea
                            ref={objectiveRef}
                            id="campaign-context-objective"
                            rows={4}
                            value={campaignObjective}
                            onChange={(event) => {
                                setFormError("");
                                setCampaignObjective(event.target.value);
                            }}
                            disabled={isSaving}
                            className="w-full resize-y rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 disabled:bg-slate-100"
                        />
                    </div>

                    <div>
                        <label
                            htmlFor="campaign-context-value-proposition"
                            className="mb-1.5 block text-sm font-semibold text-slate-700"
                        >
                            Product Value Proposition
                        </label>
                        <textarea
                            id="campaign-context-value-proposition"
                            rows={4}
                            value={productValueProposition}
                            onChange={(event) => {
                                setFormError("");
                                setProductValueProposition(event.target.value);
                            }}
                            disabled={isSaving}
                            className="w-full resize-y rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 disabled:bg-slate-100"
                        />
                    </div>

                    <div>
                        <label
                            htmlFor="campaign-context-tone"
                            className="mb-1.5 block text-sm font-semibold text-slate-700"
                        >
                            Tone Setting
                        </label>
                        <textarea
                            id="campaign-context-tone"
                            rows={3}
                            value={toneSetting}
                            onChange={(event) => {
                                setFormError("");
                                setToneSetting(event.target.value);
                            }}
                            disabled={isSaving}
                            className="w-full resize-y rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 disabled:bg-slate-100"
                        />
                    </div>
                </form>

                <footer className="flex shrink-0 flex-col-reverse gap-2 border-t border-slate-200 bg-[#FAFAFF] px-5 py-4 sm:flex-row sm:justify-end">
                    <button
                        type="button"
                        onClick={handleClose}
                        disabled={isSaving}
                        className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        form="edit-campaign-context-form"
                        disabled={isSaving}
                        className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
                    >
                        {isSaving && (
                            <Loader2
                                size={16}
                                className="animate-spin"
                                aria-hidden="true"
                            />
                        )}
                        {isSaving ? "Updating..." : "Save Changes"}
                    </button>
                </footer>
            </section>
        </div>
    );
};

export default EditCampaignContextModal;
